import * as joint from "@clientio/rappid";
import { dia, ui } from "@clientio/rappid";
import { ModelEventBus } from "../../event-bus";
import { Note } from "../mvc/note";
import { Region, RegionView } from "../mvc/region";
import { State } from "../mvc/state";
import { PreferenceStore } from "../services/preference-store";
import { DirtyState } from "../services/dirty-state";
import { activeView } from "../subdiagram-viewer";
import { getConfig } from "../../../config";
import { SUBDIAGRAM_FILTER } from "./selection-controller";
import { RappidCoreContext } from "./rappid-core-context";
import { RouterController } from "../services/layout/router-controller";
import { NameTextEditor } from "../text-editor/name-text-editor";
import MouseNavigationController from "./mouse-navigation-controller";

const { layoutEnabled } = getConfig();
export type RappidContextOptions = { customUndo: boolean; customRedo: boolean };

export default class RappidContext extends RappidCoreContext {
  paperScroller: joint.ui.PaperScroller;
  commandManager: joint.dia.CommandManager;
  snaplines: joint.ui.Snaplines;
  stencil: joint.ui.Stencil;
  clipboard: joint.ui.Clipboard;
  selection: joint.ui.Selection;
  keyboard: joint.ui.Keyboard;
  routerController: RouterController;
  tooltip: ui.Tooltip;
  nameTextEditor: NameTextEditor;
  mouseNavigationController: MouseNavigationController | null = null;
  opt: RappidContextOptions | null;

  constructor(opt?: RappidContextOptions | null) {
    super();
    this.opt = opt;

    this.createCommandManager();
    this.createNameTextEditor();
    this.createSnaplines();
    this.createPaperScroller();
    this.createStencil();
    this.createKeyboard();
    this.createClipboard();
    this.createSelection();
    this.createTooltips();
  }

  applyRouter() {
    if (!layoutEnabled) {
      // honor the feature toggle
      return;
    }
    if (!this.routerController) {
      this.routerController = new RouterController(this);
    }
  }

  createCommandManager() {
    const graph = this.graph;

    this.commandManager = new joint.dia.CommandManager({
      graph: graph,
      applyOptionsList: ["propertyPath", "mergeArrays"],
      revertOptionsList: ["propertyPath", "mergeArrays"],
      cmdBeforeAdd: function (cmdName, cell, graph, options) {
        return DirtyState.listenForChange;
      },
    });
    ModelEventBus.on("modelLoaded", (_) => {
      this.commandManager.reset();
    });
  }

  createNameTextEditor() {
    this.nameTextEditor = new NameTextEditor(this.commandManager);
  }

  createSnaplines() {
    this.snaplines = new joint.ui.Snaplines({
      paper: this.paper,
      filter: ((element: joint.dia.Element) => {
        if (!PreferenceStore.INSTANCE.get("enableSnaplines")) {
          return true;
        }
        const display = element.attr("root/display");
        const isHidden = display === "none";
        const isStatechart = element.get("type") === "Statechart";
        return isHidden || isStatechart;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any,
    });
    PreferenceStore.INSTANCE.on("change:enableSnaplines", (evt, value) => {
      value ? this.snaplines.enable() : this.snaplines.stopListening();
    });
    if (!PreferenceStore.INSTANCE.get("enableSnaplines")) {
      this.snaplines.enable();
    } else {
      this.snaplines.stopListening();
    }
  }

  createPaperScroller() {
    this.paperScroller = new joint.ui.PaperScroller({
      paper: this.paper,
      baseHeight: 300,
      baseWidth: 300,
      autoResizePaper: true,
      scrollWhileDragging: true,
      cursor: "grab",
    });
  }

  createStencil() {
    this.stencil = new GuardedStencil({
      paper: this.paperScroller,
      paperOptions: {
        cellViewNamespace: joint.shapes,
      },
      snaplines: PreferenceStore.INSTANCE.get("enableSnaplines") ? this.snaplines : null,
      dropAnimation: true,
      usePaperGrid: true,
      layout: {
        columnWidth: 30,
        columns: 1,
        rowGap: 18,
        rowHeight: "compact",
        marginX: 10,
        marginY: 10,

        resizeToFit: false,
        horizontalAlign: "middle",
        verticalAlign: "middle",
      },
      width: 50,
      height: 420,
      canDrag: () => !PreferenceStore.INSTANCE.isReadOnly,
      dragStartClone: (cell: dia.Cell): dia.Cell => {
        if (cell.get("type") === "State") return new State();
        else if (cell.get("type") === "Region") return new Region();
        else if (cell.get("type") === "Note") return new Note();
        return cell.clone().removeAttr("root/dataTooltip");
      },
    });
    PreferenceStore.INSTANCE.on("change:enableSnaplines", (evt, value) => {
      this.stencil.options.snaplines = value ? this.snaplines : null;
    });
  }

  createKeyboard() {
    this.keyboard = new joint.ui.Keyboard();
  }

  createClipboard() {
    this.clipboard = new MergingClipboard();
  }

  createSelection() {
    this.selection = new joint.ui.Selection({
      paper: this.paper,
      useModelGeometry: true,
      filter: SUBDIAGRAM_FILTER,
      translateConnectedLinks: ui.Selection.ConnectedLinksTranslation.ALL,
    });
  }

  createTooltips() {
    this.tooltip = new joint.ui.Tooltip({
      rootTarget: document.body,
      target: "[data-tooltip]",
      direction: joint.ui.Tooltip.TooltipArrowPosition.Off,
      position: joint.ui.Tooltip.TooltipPosition.Right,
      padding: 10,
      animation: true,
    });
  }
}

class GuardedStencil extends joint.ui.Stencil {
  private dropIntoParent = false;

  onDrop(cloneView: dia.CellView, evt: dia.Event) {
    if (activeView.value !== null && !(cloneView instanceof RegionView)) {
      const paper = this.options.paper as joint.dia.Paper;
      const point = paper.clientToLocalPoint(evt.clientX, evt.clientY);
      const views = paper.findElementViewsAtPoint(point);
      const parentView = views.find((v) => v !== cloneView && v.model !== cloneView.model);

      const isEmbeddingAllowed = !!parentView;

      if (!isEmbeddingAllowed) {
        this.cancelDrag();
        return;
      }
    }
    if (cloneView instanceof RegionView) {
      this.dropIntoParent = cloneView.getTargetParentView(evt) !== null;
    }
    super.onDrop(cloneView, evt);
  }

  drop(clone: dia.Element, point: dia.Point) {
    if (activeView.value !== null) {
      if (clone instanceof Region && !this.dropIntoParent) {
        const parent = activeView.value.model;
        parent.graph.once("add", (region: Region) => {
          parent.embed(region);
        });
      }
    }
    super.drop(clone, point);
    this.dropIntoParent = false;
  }
}

class MergingClipboard extends joint.ui.Clipboard {
  copyElements(selection, graph, opt?) {
    const result = super.copyElements(selection, graph, opt);
    // Clear browser's native clipboard to prevent ACE editor from intercepting paste events #1106
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText("").catch(() => {});
    }
    return result;
  }

  cutElements(selection, graph, opt?) {
    const result = super.cutElements(selection, graph, opt);
    // Clear browser's native clipboard to prevent ACE editor from intercepting paste events #1106
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText("").catch(() => {});
    }
    return result;
  }

  fetchCellsFromLocalStorage(cellNamespace) {
    const cells = this.getJSONFromStorage();
    if (!cells) return [];
    const graphJSON = {
      cells,
    };
    const tmpGraph = new joint.dia.Graph([], {
      cellNamespace,
    });
    //Added mergeArrays: true to merge arrays of cells
    tmpGraph.fromJSON(graphJSON, {
      sort: false,
      dry: true,
      mergeArrays: true,
    });
    return tmpGraph.getCells();
  }
}
